import axios, { InternalAxiosRequestConfig } from "axios";
import store from "@/store";
import config from "@/config";
import router from "@/router";
import {
  TokenDetails,
  SupportPortalRequestConfig,
  RequestConfig,
  GetRequestsQuery,
} from "@/types";
import { Module } from "@/constants";
import { emitter, NotificationType } from "@/composables/emitter";

// Initialize the axios instance for local development
export const localAxios = axios.create({
  headers: {
    channel: "ISTIO_MESH",
    "istio-workload-name": "support-portal-api-local",
    source: "SUPPORT_PORTAL",
    version: "2021-07-28",
  },
});

// Initialize the axios instance to point to proxy
let headers: { [key: string]: string } = {
  channel: "EMPLOYEE_PORTAL",
  source: "WEB_USER",
  version: "2021-07-28",
};
if (process.env.VUE_APP_DEVELOPER_VERSION)
  headers = {
    ...headers,
    developer_version: process.env.VUE_APP_DEVELOPER_VERSION,
  };
export const proxyAxios = axios.create({
  baseURL: config.supportPortalServiceUrl,
  headers: headers,
});

proxyAxios.interceptors.request.use(
  async (requestConfig: InternalAxiosRequestConfig) => {
    try {
      // skip authorization header if request is for generating token or for getting authprization url
      if (
        requestConfig.url?.includes("/auth/token") ||
        requestConfig.url?.includes("/auth/authorization-url")
      )
        return requestConfig;
      // get the token and add to authorization header
      const tokenDetails: TokenDetails = await store.dispatch("auth/getToken");
      requestConfig.headers.Authorization = `Bearer ${tokenDetails?.accessToken}`;
    } catch (error) {
      console.error(error);
    }
    return requestConfig;
  }
);

proxyAxios.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (
      error.response?.status === 401 &&
      router.currentRoute.name !== "login"
    ) {
      await store.dispatch("auth/logout");
      router.push({
        name: "login",
        query: { error: "Please login again - Invalid / Expired token" },
      });
    } else if (error.response?.status === 403) {
      emitter.$emit("notification", {
        title: "Access Denied",
        message: error.response?.data?.message,
        type: NotificationType.ERROR,
      });
      throw error;
    } else {
      throw error;
    }
  }
);

export default class RequestsService {
  module: Module;
  proxyDisabled: Boolean;
  localServices: { [key: string]: string };

  constructor(module: Module) {
    this.module = module;
    this.proxyDisabled = process.env.VUE_APP_DISABLE_PROXY === "true";
    this.localServices = JSON.parse(process.env.VUE_APP_LOCAL_SERVICES || "{}");
  }

  private validateRequest(payload: SupportPortalRequestConfig) {
    const requestConfig = payload.requestConfig;
    if (
      !requestConfig.method ||
      !requestConfig.serviceName ||
      requestConfig.route === undefined ||
      requestConfig.route === null
    ) {
      throw new Error("Request method, serviceName and route are required");
    }

    if (requestConfig.method.toLowerCase() !== "get") {
      if (!payload.entityType || !payload.entityId)
        throw new Error("entityType and entityId are required");
    }

    if (!payload.api) {
      throw new Error("api is required");
    }
  }

  private async sendMultipartFormData(payload: SupportPortalRequestConfig) {
    // stringify all other request parameters and send the files in the payload
    const requestConfig = payload.requestConfig;
    const metadata = {
      source: this.module,
      entityType: payload.entityType,
      entityId: payload.entityId,
      documentId: payload.documentId,
      freshdeskTicketId: payload.freshdeskTicketId,
      api: payload.api,
      method: requestConfig.method,
      serviceName: requestConfig.serviceName,
      route: requestConfig.route,
      headers: requestConfig.headers,
    };
    requestConfig.data.append("metadata", JSON.stringify(metadata));

    return proxyAxios.request({
      method: "POST",
      url: "/requests/multipart-form-data",
      data: requestConfig.data,
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  }

  private sendLocalRequest(requestConfig: RequestConfig) {
    // send request to service running locally
    const user = store.state.auth.user;
    return localAxios.request({
      method: requestConfig.method,
      url: this.localServices[requestConfig.serviceName] + requestConfig.route,
      params: requestConfig.query,
      data: requestConfig.data,
      headers: {
        ...requestConfig.headers,
        "source-id": user?.email,
      },
    });
  }

  async send(payload: SupportPortalRequestConfig) {
    this.validateRequest(payload);
    const requestConfig = payload.requestConfig;

    if (process.env.VUE_APP_DEVELOPER_VERSION)
      requestConfig.headers = {
        ...requestConfig.headers,
        developer_version: process.env.VUE_APP_DEVELOPER_VERSION,
      };

    if (this.proxyDisabled && this.localServices[requestConfig.serviceName])
      return this.sendLocalRequest(requestConfig);

    if (requestConfig.headers?.["Content-Type"] === "multipart/form-data")
      return this.sendMultipartFormData(payload);

    return proxyAxios.request({
      method: "POST",
      url: "/requests",
      data: {
        source: payload.source || this.module,
        entityType: payload.entityType,
        entityId: payload.entityId,
        documentId: payload.documentId,
        freshdeskTicketId: payload.freshdeskTicketId,
        api: payload.api,
        ...requestConfig,
      },
    });
  }

  async get(query: GetRequestsQuery) {
    return proxyAxios.request({
      method: "GET",
      url: "/requests",
      params: query,
    });
  }
}
